import type { DeviceGroup } from "../../../shared/models/device_group";
import { createDataStore } from "../lib/data_store";
import { post } from "aws-amplify/api";
import { API_NAME } from "../lib/amplify";
import { extractApiError } from "../lib/api_error";

const groupStore = createDataStore<DeviceGroup[] | undefined>();

export const useDeviceGroups = groupStore.useDataSuspense;
export const setDeviceGroups = groupStore.setData;

export async function addDeviceGroup(data: {
  name: string;
  certificatePolicyId: string;
}): Promise<DeviceGroup> {
  try {
    const { body } = await post({
      apiName: API_NAME,
      path: "/create-device-group",
      options: {
        body: {
          name: data.name,
          certificatePolicyId: data.certificatePolicyId,
        },
      },
    }).response;
    const group = (await body.json()) as unknown as DeviceGroup;
    groupStore.updateData((current) => [...(current ?? []), group]);
    return group;
  } catch (err) {
    throw new Error(await extractApiError(err));
  }
}

export async function associateCertificatePolicy(
  deviceGroupId: string,
  certificatePolicyId: string
): Promise<void> {
  try {
    const { body } = await post({
      apiName: API_NAME,
      path: "/associate-certificate-policy",
      options: {
        body: { deviceGroupId, certificatePolicyId },
      },
    }).response;

    const updatedGroup = (await body.json()) as unknown as DeviceGroup;
    groupStore.updateData((current) =>
      (current ?? []).map((g) =>
        g.id === deviceGroupId
          ? { ...g, certificatePolicyId: updatedGroup.certificatePolicyId }
          : g
      )
    );
  } catch (err) {
    throw new Error(await extractApiError(err));
  }
}

export async function addDeviceGroupWithKeys(data: {
  name: string;
  certificatePolicyId: string;
  keyMode: "customer-managed" | "verimatrix-managed";
  certificate?: string;
  privateKey?: string;
}): Promise<DeviceGroup> {
  try {
    const { body } = await post({
      apiName: API_NAME,
      path: "/create-device-group",
      options: {
        body: {
          name: data.name,
          certificatePolicyId: data.certificatePolicyId,
          keyMode: data.keyMode,
          ...(data.certificate != null && { certificate: data.certificate }),
          ...(data.privateKey != null && { privateKey: data.privateKey }),
        },
      },
    }).response;
    const group = (await body.json()) as unknown as DeviceGroup;
    groupStore.updateData((current) => [...(current ?? []), group]);
    return group;
  } catch (err) {
    throw new Error(await extractApiError(err));
  }
}

export async function updateKeyMode(
  deviceGroupId: string,
  keyMode: "customer-managed" | "verimatrix-managed",
  certificate?: string,
  privateKey?: string
): Promise<void> {
  try {
    const { body } = await post({
      apiName: API_NAME,
      path: "/update-device-group-key-mode",
      options: {
        body: {
          deviceGroupId,
          keyMode,
          ...(certificate != null && { certificate }),
          ...(privateKey != null && { privateKey }),
        },
      },
    }).response;

    const updatedGroup = (await body.json()) as unknown as DeviceGroup;
    groupStore.updateData((current) =>
      (current ?? []).map((g) => (g.id === deviceGroupId ? updatedGroup : g))
    );
  } catch (err) {
    throw new Error(await extractApiError(err));
  }
}

export async function deleteDeviceGroup(id: string): Promise<void> {
  try {
    await post({
      apiName: API_NAME,
      path: "/delete-device-group",
      options: { body: { id } },
    }).response;
  } catch (err) {
    throw new Error(await extractApiError(err));
  }

  groupStore.updateData((current) =>
    (current ?? []).filter((g) => !(g.id === id && !g.isDefault))
  );
}
